import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, Maximize2, X } from 'lucide-react';
import type { SessionDeck } from '@lp/shared';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { downloadDeck, useDeckFrameUrl, useDeckPdf } from '../../lib/deck.js';
import { Alert, Button, Card, IconButton, SectionHeading, Spinner, Tabs, clsx } from '../ui.js';
import { useToast } from '../Toast.js';

/**
 * A session's slide decks. One deck is shown on its own; several get a row of
 * sub-tabs, as interactive artifacts do. A deck is drawn only once its
 * sub-tab is first opened — a learner who never looks at it never downloads
 * it — and then stays mounted, only hidden, so flipping between decks keeps
 * each on the slide the reader left it.
 */
export function DecksSection({ sessionId, decks }: { sessionId: string; decks: SessionDeck[] }) {
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());

  if (!decks.length) return null;

  // A removed deck falls back to the first one rather than leaving no
  // sub-tab selected.
  const activeId = decks.some((d) => d.id === chosenId) ? chosenId! : decks[0]!.id;
  // Adjusting state while rendering: remember that this deck has been opened.
  if (!opened.has(activeId)) setOpened(new Set(opened).add(activeId));

  if (decks.length === 1) return <DeckViewer sessionId={sessionId} deck={decks[0]!} />;

  return (
    <div>
      <Tabs
        className="mb-4"
        variant="pill"
        idPrefix="deck"
        label="Slide decks"
        active={activeId}
        onChange={setChosenId}
        tabs={decks.map((deck) => ({
          id: deck.id,
          title: deck.title,
          label: <span className="max-w-[16rem] truncate">{deck.title}</span>,
        }))}
      />
      {decks.map((deck) => (
        <div
          key={deck.id}
          role="tabpanel"
          id={`deck-panel-${deck.id}`}
          aria-labelledby={`deck-tab-${deck.id}`}
          hidden={deck.id !== activeId}
        >
          {opened.has(deck.id) || deck.id === activeId ? (
            <DeckViewer sessionId={sessionId} deck={deck} />
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * One slide deck, drawn by the viewer its format needs: a PDF here,
 * page by page, or an HTML presentation — one page or a zipped folder — in a
 * sandboxed frame.
 */
export function DeckViewer({ sessionId, deck }: { sessionId: string; deck: SessionDeck }) {
  return deck.format !== 'pdf' ? (
    <HtmlDeck sessionId={sessionId} deck={deck} />
  ) : (
    <PdfDeck sessionId={sessionId} deck={deck} />
  );
}

const FORMAT_LABEL: Record<SessionDeck['format'], string> = {
  pdf: 'PDF',
  html: 'HTML',
  zip: 'ZIP',
};

/** "Download .pptx" when the original is attached, else the deck's own file. */
function DownloadDeckButton({ sessionId, deck }: { sessionId: string; deck: SessionDeck }) {
  const { toastError } = useToast();
  const download = (): void => {
    downloadDeck(sessionId, deck.id, deck.source ? 'source' : 'deck').catch((err: unknown) =>
      toastError(err, 'The deck could not be downloaded.'),
    );
  };
  const sourceExt = deck.source?.fileName.split('.').pop()?.toLowerCase();
  return (
    <button
      type="button"
      onClick={download}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-text hover:underline"
    >
      <Download size={14} aria-hidden />
      {sourceExt
        ? `Download .${sourceExt}`
        : `Download ${FORMAT_LABEL[deck.format]}`}
    </button>
  );
}

function DeckOpening({ deck }: { deck: SessionDeck }) {
  return (
    <section aria-labelledby={`deck-heading-${deck.id}`}>
      <SectionHeading id={`deck-heading-${deck.id}`}>{deck.title}</SectionHeading>
      <Card className="flex aspect-video items-center justify-center gap-3 text-sm text-ink-muted">
        <Spinner />
        Opening the slides…
      </Card>
    </section>
  );
}

function DeckUnavailable({ deck, retry }: { deck: SessionDeck; retry: () => void }) {
  return (
    <section aria-labelledby={`deck-heading-${deck.id}`}>
      <SectionHeading id={`deck-heading-${deck.id}`}>{deck.title}</SectionHeading>
      <Alert tone="danger" title="The slides could not be opened">
        <p>Check your connection and try again.</p>
        <Button size="sm" variant="secondary" className="mt-2" onClick={retry}>
          Try again
        </Button>
      </Alert>
    </section>
  );
}

/**
 * An HTML deck: the trainer's own presentation, run as-is in a frame on the
 * sandbox origin with scripts and nothing else — no same-origin access, no
 * forms, no popups, no top-level navigation. The deck brings its own
 * navigation; the app cannot reach inside the frame to drive it, so there are
 * no previous / next buttons or thumbnails here, only "Present".
 *
 * "Present" fills the screen with the same frame — fullscreen where the
 * browser allows it, a full-window overlay where it does not — so the deck
 * stays on the slide the reader was on. Focus goes into the frame, so the
 * deck's own keys work straight away, and a deck that understands it is told
 * it is being presented, so it can hide its own editing chrome.
 */
function HtmlDeck({ sessionId, deck }: { sessionId: string; deck: SessionDeck }) {
  const frameUrl = useDeckFrameUrl(sessionId, deck);
  const [presenting, setPresenting] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const presentButtonRef = useRef<HTMLButtonElement>(null);

  const startPresenting = (): void => {
    flushSync(() => setPresenting(true));
    const stage = stageRef.current;
    if (stage?.requestFullscreen) {
      stage.requestFullscreen().catch(() => {
        // Refused: the overlay is already up and works the same.
      });
    }
    frameRef.current?.focus();
  };

  const leave = (): void => {
    setPresenting(false);
    presentButtonRef.current?.focus();
  };

  const stopPresenting = (): void => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    leave();
  };

  // Esc in fullscreen leaves fullscreen even with focus inside the frame;
  // follow it out of presenting. In the overlay fallback Esc reaches the page
  // only while focus is outside the frame, so the exit control stays in reach.
  useEffect(() => {
    if (!presenting) return;
    const onChange = (): void => {
      if (!document.fullscreenElement) leave();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !document.fullscreenElement) leave();
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [presenting]);

  // Decks built with Claude Design (`deck-stage`) hide their editing chrome —
  // the thumbnail rail — when their host says it is presenting. Say so while
  // presenting, and again every second: a deck that is still unpacking when
  // "Present" is pressed has no listener yet, and the deck ignores a repeat
  // of what it already knows. Any other deck ignores the message.
  useEffect(() => {
    const tell = (): void =>
      frameRef.current?.contentWindow?.postMessage({ __omelette_presenting: presenting }, '*');
    tell();
    if (!presenting) return;
    const timer = window.setInterval(tell, 1000);
    return () => window.clearInterval(timer);
  }, [presenting]);

  if (frameUrl.isPending) return <DeckOpening deck={deck} />;
  if (frameUrl.isError) {
    return <DeckUnavailable deck={deck} retry={() => void frameUrl.refetch()} />;
  }

  return (
    <section aria-labelledby={`deck-heading-${deck.id}`}>
      <SectionHeading
        id={`deck-heading-${deck.id}`}
        action={
          <div className="flex items-center gap-4">
            <DownloadDeckButton sessionId={sessionId} deck={deck} />
            <Button
              ref={presentButtonRef}
              size="sm"
              variant="secondary"
              icon={<Maximize2 size={14} aria-hidden />}
              onClick={startPresenting}
            >
              Present
            </Button>
          </div>
        }
      >
        {deck.title}
      </SectionHeading>

      <Card className="overflow-hidden">
        {/* One element is both the inline box and the presenting stage, so the
            frame is never re-parented: moving an iframe reloads its deck. */}
        <div
          ref={stageRef}
          role={presenting ? 'dialog' : undefined}
          aria-modal={presenting ? 'true' : undefined}
          aria-label={presenting ? `${deck.title} — presenting` : undefined}
          className={clsx(
            'group bg-black',
            presenting ? 'fixed inset-0 z-50' : 'relative aspect-video w-full',
          )}
        >
          <iframe
            ref={frameRef}
            src={frameUrl.data}
            title={deck.title}
            sandbox="allow-scripts"
            allowFullScreen
            referrerPolicy="no-referrer"
            className="block h-full w-full border-0"
          />
          {presenting ? (
            <button
              type="button"
              aria-label="Exit presentation"
              title="Exit presentation (Esc)"
              onClick={stopPresenting}
              className="absolute right-4 top-4 rounded-full bg-black/70 p-2 text-white opacity-0 transition-opacity duration-200 hover:bg-black/85 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
            >
              <X size={18} aria-hidden />
            </button>
          ) : null}
        </div>
        <p className="border-t border-line px-4 py-3 text-xs text-ink-subtle">
          Click the slides, then move through them with the deck’s own controls or arrow keys.
        </p>
      </Card>
    </section>
  );
}

/**
 * A PDF deck: one slide at a time with previous / next, a strip of thumbnails
 * to jump anywhere, and the original file to download.
 *
 * The deck is the PDF the trainer uploaded, drawn page by page with pdf.js, so
 * what learners see is exactly what was exported. Thumbnails are drawn only as
 * they scroll into view.
 *
 * The arrow keys page through the deck while focus is inside the viewer, not
 * page-wide, so they never fight the video player or a quiz for the keyboard.
 *
 * "Present" shows the current slide alone on a black screen, in real
 * fullscreen where the browser allows it and as a full-window overlay where it
 * does not (iOS Safari only fullscreens video). While presenting, the keyboard
 * is page-wide — nothing else is on screen to fight for it — and a click on
 * the slide advances, like a clicker. Leaving returns to the same slide.
 */
function PdfDeck({ sessionId, deck }: { sessionId: string; deck: SessionDeck }) {
  const pdf = useDeckPdf(sessionId, deck);

  if (pdf.isPending) return <DeckOpening deck={deck} />;
  if (pdf.isError) return <DeckUnavailable deck={deck} retry={() => void pdf.refetch()} />;
  return <Slides sessionId={sessionId} deck={deck} doc={pdf.data} />;
}

function Slides({
  sessionId,
  deck,
  doc,
}: {
  sessionId: string;
  deck: SessionDeck;
  doc: PDFDocumentProxy;
}) {
  const [index, setIndex] = useState(0);
  const [presenting, setPresenting] = useState(false);
  const [ratio, setRatio] = useState(16 / 9);
  const stripRef = useRef<HTMLOListElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const presentButtonRef = useRef<HTMLButtonElement>(null);
  const count = doc.numPages;
  const pageNumber = index + 1;

  const go = (next: number): void => setIndex(Math.min(count - 1, Math.max(0, next)));

  // Every box is sized from the first page, so nothing jumps as slides draw.
  useEffect(() => {
    let live = true;
    void doc.getPage(1).then((page) => {
      const { width, height } = page.getViewport({ scale: 1 });
      if (live && width > 0 && height > 0) setRatio(width / height);
    });
    return () => {
      live = false;
    };
  }, [doc]);

  // A replaced deck can be shorter than the slide the reader was on.
  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(0, count - 1)));
  }, [count]);

  const startPresenting = (): void => {
    // The stage has to exist before fullscreen is requested, and the request
    // has to happen inside the click — so render synchronously, then ask.
    flushSync(() => setPresenting(true));
    const stage = stageRef.current;
    if (stage?.requestFullscreen) {
      stage.requestFullscreen().catch(() => {
        // Refused (an iframe without allowfullscreen, a strict browser): the
        // overlay is already up and works the same.
      });
    }
    stage?.focus();
  };

  const leave = (): void => {
    setPresenting(false);
    presentButtonRef.current?.focus();
  };

  const stopPresenting = (): void => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    leave();
  };

  // Esc in fullscreen is handled by the browser, which leaves fullscreen
  // without the keydown listener ever seeing it; follow it out of presenting.
  useEffect(() => {
    if (!presenting) return;
    const onChange = (): void => {
      if (!document.fullscreenElement) leave();
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [presenting]);

  useEffect(() => {
    if (!presenting) return;
    const onKey = (event: KeyboardEvent): void => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
        case 'Enter':
          setIndex((i) => Math.min(count - 1, i + 1));
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
        case 'Backspace':
          setIndex((i) => Math.max(0, i - 1));
          break;
        case 'Home':
          setIndex(0);
          break;
        case 'End':
          setIndex(count - 1);
          break;
        case 'Escape':
          // Only reached in the overlay fallback; see the effect above.
          leave();
          break;
        default:
          return;
      }
      event.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    // The page behind the overlay should not scroll under the reader's wheel.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [presenting, count]);

  // Keep the current thumbnail in view as the reader pages past the strip's edge.
  useEffect(() => {
    const thumb = stripRef.current?.children[index] as HTMLElement | undefined;
    thumb?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [index]);

  return (
    <section aria-labelledby={`deck-heading-${deck.id}`}>
      <SectionHeading
        id={`deck-heading-${deck.id}`}
        action={
          <div className="flex items-center gap-4">
            <DownloadDeckButton sessionId={sessionId} deck={deck} />
            <Button
              ref={presentButtonRef}
              size="sm"
              variant="secondary"
              icon={<Maximize2 size={14} aria-hidden />}
              onClick={startPresenting}
            >
              Present
            </Button>
          </div>
        }
      >
        {deck.title}
      </SectionHeading>

      <Card
        className="overflow-hidden"
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') go(index + 1);
          else if (event.key === 'ArrowLeft') go(index - 1);
          else return;
          event.preventDefault();
        }}
      >
        <div className="bg-surface-2 border-b border-line">
          <PdfPage
            doc={doc}
            pageNumber={pageNumber}
            label={`Slide ${pageNumber} of ${count}`}
            className="w-full"
            style={{ aspectRatio: ratio }}
          />
        </div>

        <div className="flex items-center gap-3 px-4 py-3">
          <IconButton
            label="Previous slide"
            variant="secondary"
            onClick={() => go(index - 1)}
            disabled={index === 0}
          >
            <ChevronLeft size={16} aria-hidden />
          </IconButton>
          <p className="min-w-0 flex-1 text-sm" aria-live="polite">
            <span className="font-mono text-xs text-ink-subtle">
              {pageNumber} / {count}
            </span>
          </p>
          <IconButton
            label="Next slide"
            variant="secondary"
            onClick={() => go(index + 1)}
            disabled={index === count - 1}
          >
            <ChevronRight size={16} aria-hidden />
          </IconButton>
        </div>

        <ol
          ref={stripRef}
          aria-label="All slides"
          className="flex gap-2 overflow-x-auto border-t border-line px-4 py-3"
        >
          {Array.from({ length: count }, (_, i) => (
            <li key={i} className="shrink-0">
              <button
                type="button"
                onClick={() => setIndex(i)}
                aria-current={i === index ? 'true' : undefined}
                aria-label={`Slide ${i + 1}`}
                className={clsx(
                  'block w-28 rounded-control border-2 overflow-hidden bg-surface-2 transition-colors',
                  i === index ? 'border-brand' : 'border-line hover:border-line-strong',
                )}
              >
                <PdfPage
                  doc={doc}
                  pageNumber={i + 1}
                  lazy
                  className="w-full"
                  style={{ aspectRatio: ratio }}
                />
              </button>
            </li>
          ))}
        </ol>
      </Card>

      {presenting ? (
        <div
          ref={stageRef}
          role="dialog"
          aria-modal="true"
          aria-label={`${deck.title} — presenting`}
          tabIndex={-1}
          className="group fixed inset-0 z-50 flex items-center justify-center bg-black outline-none"
        >
          <PdfPage
            doc={doc}
            pageNumber={pageNumber}
            label={`Slide ${pageNumber} of ${count}`}
            onClick={() => go(index + 1)}
            className="h-full w-full cursor-pointer select-none"
          />
          {/* Out of the way until the pointer comes near, so the slide owns the screen. */}
          <div className="absolute inset-x-0 bottom-0 flex justify-center p-4 opacity-0 transition-opacity duration-200 hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
            <div className="flex items-center gap-3 rounded-full bg-black/70 px-3 py-1.5 text-white">
              <button
                type="button"
                aria-label="Previous slide"
                onClick={() => go(index - 1)}
                disabled={index === 0}
                className="rounded-full p-1.5 hover:bg-white/15 disabled:opacity-40"
              >
                <ChevronLeft size={18} aria-hidden />
              </button>
              <span className="font-mono text-xs tabular-nums" aria-live="polite">
                {pageNumber} / {count}
              </span>
              <button
                type="button"
                aria-label="Next slide"
                onClick={() => go(index + 1)}
                disabled={index === count - 1}
                className="rounded-full p-1.5 hover:bg-white/15 disabled:opacity-40"
              >
                <ChevronRight size={18} aria-hidden />
              </button>
              <span className="mx-1 h-4 w-px bg-white/30" aria-hidden />
              <button
                type="button"
                aria-label="Exit presentation"
                title="Exit presentation (Esc)"
                onClick={stopPresenting}
                className="rounded-full p-1.5 hover:bg-white/15"
              >
                <X size={18} aria-hidden />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * One PDF page drawn to fit its box, sharp on high-density screens. Each draw
 * goes to a fresh canvas that replaces the old one only once it is finished,
 * so a resize or a page change never flashes a blank slide, and two draws never
 * fight over one canvas. A `lazy` page waits until it scrolls into view.
 */
function PdfPage({
  doc,
  pageNumber,
  label,
  lazy = false,
  className,
  style,
  onClick,
}: {
  doc: PDFDocumentProxy;
  pageNumber: number;
  label?: string;
  lazy?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  const [visible, setVisible] = useState(!lazy);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setBox((prev) =>
        prev && Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1
          ? prev
          : { width, height },
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (visible) return;
    const el = boxRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setVisible(true);
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || !box || !visible || box.width < 1 || box.height < 1) return;
    let task: RenderTask | null = null;
    let cancelled = false;

    void doc.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const fit = Math.min(box.width / base.width, box.height / base.height);
      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: fit * dpr });

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.floor(base.width * fit)}px`;
      canvas.style.height = `${Math.floor(base.height * fit)}px`;
      canvas.style.display = 'block';

      task = page.render({ canvas, viewport });
      task.promise.then(
        () => {
          if (!cancelled) el.replaceChildren(canvas);
        },
        () => {
          // Cancelled by a newer draw, or a page pdf.js could not draw: the
          // previous canvas, if there is one, stays.
        },
      );
    });

    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, pageNumber, box, visible]);

  return (
    <div
      ref={boxRef}
      role={label ? 'img' : undefined}
      aria-label={label}
      onClick={onClick}
      className={clsx('flex items-center justify-center overflow-hidden', className)}
      style={style}
    />
  );
}
